import keycloakClient from "@keycloak/keycloak";
import { BACKEND_API_URL } from "../../config";
import type {
  ChatRequestPayload,
  ChatResponse,
  StreamCallbacks,
  StreamEvent
} from "./models";

class ChatService {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeaders(): HeadersInit {
    return {
      Authorization: `Bearer ${keycloakClient.token}`,
      Accept: "application/json"
    };
  }

  private getStreamHeaders(): HeadersInit {
    return {
      Authorization: `Bearer ${keycloakClient.token}`,
      Accept: "text/event-stream"
    };
  }

  /**
   * Prepara un FormData para enviar al servidor
   * @param payload Los datos para enviar
   * @returns FormData preparado
   */
  private prepareFormData(payload: ChatRequestPayload): FormData {
    const { session_id, user_input, user_voice_record, language } = payload;

    const formData = new FormData();
    formData.append("session_id", session_id);
    formData.append("language", language);

    if (user_input) {
      formData.append("user_input", user_input);
    }

    if (user_voice_record) {
      formData.append(
        "user_voice_record",
        user_voice_record,
        "voice-recording.mp3"
      );
    }

    return formData;
  }

  private handleApiError(error: any): never {
    console.error("Error en la comunicación con el API de chat:", error);
    throw error;
  }

  async sendChatMessage(payload: ChatRequestPayload): Promise<ChatResponse> {
    try {
      const formData = this.prepareFormData(payload);

      const response = await fetch(`${this.baseUrl}/chat`, {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: formData
      });

      if (!response.ok) {
        throw new Error(
          `Error en la petición: ${response.status} ${response.statusText}`
        );
      }

      return (await response.json()) as ChatResponse;
    } catch (error) {
      this.handleApiError(error);
    }
  }

  /**
   * Procesa el stream SSE desde la API y dispara los callbacks.
   */
  private async processStream(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    callbacks: StreamCallbacks
  ): Promise<void> {
    const decoder = new TextDecoder();
    let buffer = "";
    let currentEvent = "";

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");

        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("event: ")) {
            currentEvent = line.substring(7).trim();
            continue;
          }

          if (line.startsWith("data: ")) {
            const jsonData = line.substring(6);

            try {
              const eventData: StreamEvent = JSON.parse(jsonData);
              if (!eventData.type && currentEvent) {
                eventData.type = currentEvent as StreamEvent["type"];
              }

              this.handleEvent(eventData, callbacks);
              currentEvent = "";
            } catch (e) {
              console.error("Error parsing JSON from stream:", e, jsonData);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error reading stream:", error);
      if (callbacks.onError) {
        callbacks.onError("error");
      }
      throw error;
    }
  }

  private handleEvent(eventData: StreamEvent, callbacks: StreamCallbacks): void {
    const { type } = eventData;

    switch (type) {
      case "status":
        if (callbacks.onStatus && eventData.message) {
          callbacks.onStatus(eventData.message);
        }
        break;

      case "token":
        if (callbacks.onToken && eventData.content) {
          callbacks.onToken(eventData.content);
        }
        break;

      case "tool_start":
        if (callbacks.onToolStart && eventData.tool && eventData.message) {
          callbacks.onToolStart(eventData.tool, eventData.message);
        }
        break;

      case "tool_end":
        if (callbacks.onToolEnd && eventData.tool) {
          callbacks.onToolEnd(eventData.tool, eventData.preview);
        }
        break;

      case "final":
        if (callbacks.onFinal && eventData.message) {
          callbacks.onFinal(eventData.message);
        }
        break;

      case "transcription":
        if (callbacks.onTranscription && eventData.content) {
          callbacks.onTranscription(eventData.content);
        }
        break;

      case "metadata":
        if (callbacks.onMetadata && eventData.sources) {
          callbacks.onMetadata(eventData.sources);
        }
        break;

      case "error":
        console.error("Error event from stream:", eventData.message);
        if (callbacks.onError) {
          callbacks.onError("error");
        }
        break;

      default:
    }
  }

  private async streamWithFormData(
    formData: FormData,
    callbacks: StreamCallbacks = {}
  ): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/chat/stream`, {
        method: "POST",
        headers: this.getStreamHeaders(),
        body: formData
      });

      if (!response.ok) {
        throw new Error(
          `Error en la petición de streaming: ${response.status} ${response.statusText}`
        );
      }

      if (!response.body) {
        throw new Error("Cuerpo de respuesta vacío en streaming");
      }

      const reader = response.body.getReader();
      await this.processStream(reader, callbacks);
    } catch (error) {
      console.error("Error connecting to chat stream:", error);
      if (callbacks.onError) {
        callbacks.onError("error");
      }
      throw error;
    }
  }

  /**
   * Envía un mensaje al chat basado en texto o audio y consume el stream SSE.
   */
  async streamChatRequestWithAudioOrText(
    sessionId: string,
    language: string,
    text?: string,
    audioBlob?: Blob | null,
    callbacks: StreamCallbacks = {}
  ): Promise<void> {
    const formData = new FormData();
    formData.append("session_id", sessionId);
    formData.append("language", language);

    if (text) {
      formData.append("user_input", text);
    }

    if (audioBlob) {
      formData.append("user_voice_record", audioBlob, "voice-recording.mp3");
    }

    await this.streamWithFormData(formData, callbacks);
  }

  /**
   * Envía un mensaje al chat basado en texto o audio (respuesta no streaming).
   */
  async sendChatRequestWithAudioOrText(
    sessionId: string,
    language: string,
    text?: string,
    audioBlob?: Blob | null
  ): Promise<ChatResponse> {
    try {
      if (audioBlob) {
        return await this.sendChatMessage({
          session_id: sessionId,
          language,
          user_voice_record: audioBlob
        });
      }

      return await this.sendChatMessage({
        session_id: sessionId,
        language,
        user_input: text
      });
    } catch (error) {
      console.error("Error sending chat request:", error);
      return { response: "" };
    }
  }
}

export const chatService = new ChatService(`${BACKEND_API_URL}/chatbot-api/v1`);
